import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Html, OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three";

const PALETTE = ["#FF2BD6", "#CCFF00", "#37E5D7", "#FFE600", "#FF8A00", "#7CFF4F", "#00B3FF", "#FF4D6D", "#B84DFF", "#00FFC6", "#FFFFFF", "#FFB3F0"];
const ANAT = ["NEO-CORTEX", "PARIETAL", "FRONTAL", "TEMPORAL", "OCCIPITAL", "HIPPOCAMPUS", "THALAMUS", "CEREBELLUM", "AMYGDALA", "INSULA", "BROCA", "WERNICKE"];
const PER_REGION = 420;
const CORE_N = 420;
const MAX_R = 13;
const MAX_SPIKES = 180;
const SPEECH = /BROCA|WERNICKE|TEMPORAL|FRONTAL|INSULA/;

const rng = (s) => () => ((s = (s * 16807) % 2147483647) / 2147483647);
const gauss = (r) => Math.sqrt(-2 * Math.log(r() + 1e-6)) * Math.cos(2 * Math.PI * r());

const VERT = `
attribute float aSize; attribute vec3 aColor; attribute float aRegion;
uniform float uI[${MAX_R}]; uniform float uTime;
varying vec3 vColor; varying float vA;
void main(){
  float inten = uI[int(aRegion)];
  vec3 p = position + normalize(position + 0.001) * sin(uTime * 2.0 + aSize * 9.0) * 0.012 * (1.0 + inten * 2.0);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_PointSize = aSize * (260.0 / -mv.z) * (0.75 + inten * 0.7);
  gl_Position = projectionMatrix * mv;
  vColor = aColor * (0.3 + inten * 1.25);
  vA = 0.22 + inten * 0.78;
}`;
const FRAG = `
varying vec3 vColor; varying float vA;
void main(){
  float d = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.0, d);
  gl_FragColor = vec4(vColor, a * a * vA);
}`;

function buildBrain(regions) {
  const r = rng(11);
  const anchors = regions.map((name, i) => {
    const phi = Math.acos(1 - (2 * (i + 0.5)) / regions.length) * 0.82;
    const th = Math.PI * (1 + Math.sqrt(5)) * i;
    const dir = new THREE.Vector3(Math.sin(phi) * Math.cos(th), Math.cos(phi) * 0.9 - 0.1, Math.sin(phi) * Math.sin(th)).normalize();
    return { name, label: `S${i + 1} ${ANAT[i % ANAT.length]}`, color: PALETTE[i % PALETTE.length],
      pos: new THREE.Vector3(dir.x * 1.7, dir.y * 1.2, dir.z * 1.42) };
  });
  const total = anchors.length * PER_REGION + CORE_N;
  const pos = new Float32Array(total * 3), col = new Float32Array(total * 3), size = new Float32Array(total), reg = new Float32Array(total);
  const c = new THREE.Color();
  let k = 0;
  const put = (v, color, s, ri) => {
    pos.set([v.x, v.y, v.z], k * 3);
    c.set(color).lerp(new THREE.Color("#FFFFFF"), r() * 0.25);
    col.set([c.r, c.g, c.b], k * 3);
    size[k] = s; reg[k] = ri; k++;
  };
  anchors.forEach((a, ri) => {
    for (let j = 0; j < PER_REGION; j++) {
      const v = a.pos.clone().add(new THREE.Vector3(gauss(r), gauss(r), gauss(r)).multiplyScalar(0.3));
      put(v, a.color, 0.09 + r() * 0.22, ri);
    }
  });
  for (let j = 0; j < CORE_N; j++) {
    const v = new THREE.Vector3(gauss(r), gauss(r) * 0.8, gauss(r)).multiplyScalar(0.32);
    put(v, r() > 0.5 ? "#FFE600" : "#FFFFFF", 0.06 + r() * 0.14, anchors.length);
  }
  const lat = [], latC = [];
  const lc = new THREE.Color();
  const seg = (a, b, color) => { lat.push(a.x, a.y, a.z, b.x, b.y, b.z); lc.set(color); latC.push(lc.r, lc.g, lc.b, lc.r, lc.g, lc.b); };
  anchors.forEach((a, i) => {
    seg(a.pos, new THREE.Vector3(), a.color);
    const near = anchors.map((b, j) => [j, a.pos.distanceTo(b.pos)]).filter(([j]) => j !== i).sort((x, y) => x[1] - y[1]).slice(0, 2);
    near.forEach(([j]) => seg(a.pos, anchors[j].pos, "#FFE600"));
  });
  for (let j = 0; j < 260; j++) {
    const a = new THREE.Vector3(gauss(r), gauss(r), gauss(r)).multiplyScalar(0.55);
    const b = a.clone().add(new THREE.Vector3(gauss(r), gauss(r), gauss(r)).multiplyScalar(0.35));
    seg(a, b, ["#FFE600", "#FFFFFF", "#FF2BD6", "#37E5D7"][j % 4]);
  }
  return { anchors, pos, col, size, reg, lattice: new Float32Array(lat), latticeColors: new Float32Array(latC) };
}

const Room = () => (
  <group>
    <gridHelper args={[18, 36, "#37E5D7", "#0F5C55"]} position={[0, -2.4, 0]} />
    <gridHelper args={[18, 36, "#37E5D7", "#0F5C55"]} position={[0, 2.6, -7]} rotation={[Math.PI / 2, 0, 0]} />
    <gridHelper args={[18, 36, "#0F5C55", "#0B3F3A"]} position={[-8, 2.6, 0]} rotation={[0, 0, Math.PI / 2]} />
    <mesh><sphereGeometry args={[2.45, 28, 18]} /><meshBasicMaterial color="#37E5D7" wireframe transparent opacity={0.09} /></mesh>
    <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -2.38, 0]}><ringGeometry args={[2.3, 2.34, 96]} /><meshBasicMaterial color="#37E5D7" transparent opacity={0.5} /></mesh>
  </group>
);

const Brain = ({ regions, scores, linkState, burstRef, onSpike, timeLapse, levelRef }) => {
  const g = useMemo(() => buildBrain(regions), [regions]);
  const group = useRef();
  const flash = useRef(new Array(MAX_R).fill(0));
  const spikes = useRef([]);
  const spikeGeo = useRef();
  const uniforms = useMemo(() => ({ uI: { value: new Array(MAX_R).fill(0.3) }, uTime: { value: 0 } }), []);
  const spikePos = useMemo(() => new Float32Array(MAX_SPIKES * 3), []);
  const spikeCol = useMemo(() => new Float32Array(MAX_SPIKES * 3), []);
  const trailPos = useMemo(() => new Float32Array(MAX_SPIKES * 6), []);
  const trailCol = useMemo(() => new Float32Array(MAX_SPIKES * 6), []);
  const trailGeo = useRef();
  const speechIdx = useMemo(() => {
    const idx = g.anchors.map((a, i) => (SPEECH.test(a.label) ? i : -1)).filter((i) => i >= 0);
    return idx.length ? idx : g.anchors.map((_, i) => i);
  }, [g]);
  const acc = useRef(0);

  const fire = (from, to) => {
    if (spikes.current.length >= MAX_SPIKES) return;
    const A = from < g.anchors.length ? g.anchors[from].pos : new THREE.Vector3();
    const B = to < g.anchors.length ? g.anchors[to].pos : new THREE.Vector3();
    spikes.current.push({ from, to, A, B, t: 0, color: new THREE.Color(from < g.anchors.length ? g.anchors[from].color : "#FFE600") });
    flash.current[from] = 1;
  };

  useFrame((state, dt) => {
    const t = state.clock.getElapsedTime();
    uniforms.uTime.value = t;
    const n = g.anchors.length;
    const lv = levelRef?.current || 0;
    const speaking = linkState === "speaking";
    const rate = (linkState === "thinking" ? 9 : speaking ? 6 + lv * 70 : 0.7) * (timeLapse ? 3 : 1);
    acc.current += dt * rate;
    while (acc.current > 1) {
      acc.current -= 1;
      const from = speaking && Math.random() < 0.7 ? speechIdx[Math.floor(Math.random() * speechIdx.length)] : Math.floor(Math.random() * (n + 1));
      let to = Math.random() < 0.45 ? n : Math.floor(Math.random() * n);
      if (to === from) to = from === n ? Math.floor(Math.random() * n) : n;
      fire(from, to);
    }
    while (burstRef.current.length) {
      const name = burstRef.current.shift();
      const idx = g.anchors.findIndex((a) => a.name === name);
      if (idx >= 0) for (let i = 0; i < 6; i++) fire(idx, i % 2 ? n : Math.floor(Math.random() * n));
    }
    const live = [];
    spikes.current.forEach((s) => {
      s.t += dt * (timeLapse ? 3.2 : 1.6);
      if (s.t >= 1) {
        flash.current[s.to] = 1;
        onSpike(s.from < n ? g.anchors[s.from] : { label: "LIMBIC CORE", color: "#FFE600" }, s.to < n ? g.anchors[s.to] : { label: "LIMBIC CORE", color: "#FFE600" });
      } else live.push(s);
    });
    spikes.current = live;
    spikePos.fill(9999);
    trailPos.fill(9999);
    const arc = (sp, t) => { const p = sp.A.clone().lerp(sp.B, t); p.y += Math.sin(t * Math.PI) * 0.25; return p; };
    live.forEach((sp, i) => {
      const p = arc(sp, sp.t);
      const q = arc(sp, Math.max(0, sp.t - 0.22));
      const glow = 2 + lv * 2;
      spikePos.set([p.x, p.y, p.z], i * 3);
      spikeCol.set([sp.color.r * glow, sp.color.g * glow, sp.color.b * glow], i * 3);
      trailPos.set([q.x, q.y, q.z, p.x, p.y, p.z], i * 6);
      trailCol.set([0, 0, 0, sp.color.r * 1.6, sp.color.g * 1.6, sp.color.b * 1.6], i * 6);
    });
    [spikeGeo, trailGeo].forEach((ref) => {
      if (!ref.current) return;
      ref.current.attributes.position.needsUpdate = true;
      ref.current.attributes.color.needsUpdate = true;
    });
    for (let i = 0; i <= n; i++) {
      flash.current[i] = Math.max(0, flash.current[i] - dt * 1.6);
      const base = i < n ? 0.46 + (scores[g.anchors[i].name] || 0) * 0.5 : 0.6;
      const think = linkState === "thinking" ? 0.25 * (0.5 + 0.5 * Math.sin(t * 7 + i)) : 0;
      const voice = speaking ? lv * (speechIdx.includes(i) ? 0.9 : 0.35) : 0;
      uniforms.uI.value[i] = Math.min(1.6, base + flash.current[i] * 0.8 + think + voice);
    }
    group.current.rotation.y += dt * 0.04;
  });

  return (
    <group ref={group}>
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[g.pos, 3]} />
          <bufferAttribute attach="attributes-aColor" args={[g.col, 3]} />
          <bufferAttribute attach="attributes-aSize" args={[g.size, 1]} />
          <bufferAttribute attach="attributes-aRegion" args={[g.reg, 1]} />
        </bufferGeometry>
        <shaderMaterial vertexShader={VERT} fragmentShader={FRAG} uniforms={uniforms} transparent depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      <lineSegments>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[g.lattice, 3]} />
          <bufferAttribute attach="attributes-color" args={[g.latticeColors, 3]} />
        </bufferGeometry>
        <lineBasicMaterial vertexColors transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} />
      </lineSegments>
      <lineSegments>
        <bufferGeometry ref={trailGeo}>
          <bufferAttribute attach="attributes-position" args={[trailPos, 3]} />
          <bufferAttribute attach="attributes-color" args={[trailCol, 3]} />
        </bufferGeometry>
        <lineBasicMaterial vertexColors transparent opacity={0.95} blending={THREE.AdditiveBlending} depthWrite={false} />
      </lineSegments>
      <points>
        <bufferGeometry ref={spikeGeo}>
          <bufferAttribute attach="attributes-position" args={[spikePos, 3]} />
          <bufferAttribute attach="attributes-color" args={[spikeCol, 3]} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.16} sizeAttenuation transparent depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      {g.anchors.map((a) => (
        <Html key={a.name} position={[a.pos.x * 1.18, a.pos.y * 1.18 + 0.3, a.pos.z * 1.18]} center distanceFactor={6} zIndexRange={[5, 0]}>
          <div className="pointer-events-none whitespace-nowrap font-mono font-extrabold leading-none" style={{ color: a.color, fontSize: 13, textShadow: `0 0 10px ${a.color}` }}>
            {a.label}<div className="font-medium opacity-75 mt-0.5" style={{ fontSize: 9, letterSpacing: "0.2em" }}>{a.name}</div>
          </div>
        </Html>
      ))}
      <Html position={[0, 0.5, 0]} center distanceFactor={7} zIndexRange={[5, 0]}>
        <div className="pointer-events-none whitespace-nowrap font-mono font-extrabold" style={{ color: "#FFE600", fontSize: 13, textShadow: "0 0 10px #FFE600" }}>LIMBIC CORE</div>
      </Html>
    </group>
  );
};

const Telemetry = ({ rate, coherence, focus, log }) => (
  <div className="absolute bottom-8 left-3 w-60 border border-white/10 bg-black/75 backdrop-blur-md p-3 font-mono text-[9.5px] tracking-[0.1em] z-10" data-testid="synaptic-telemetry">
    <p className="text-accent2 tracking-[0.2em]">SYNAPTIC TELEMETRY · LIVE</p>
    <p className="mt-2 text-white/80">SPIKE RATE <span className="text-white">{rate.toFixed(1)}/s</span></p>
    <p className="text-white/80">NETWORK COHERENCE <span className="text-white">{coherence}%</span></p>
    <p className="text-white/80">ACTIVE FOCUS <span className="text-brand">{focus}</span></p>
    <ul className="mt-2 space-y-0.5">
      {log.length === 0 && <li className="text-white/30">awaiting first spike…</li>}
      {log.map((l, i) => (
        <li key={i} className="truncate" style={{ color: l.color, opacity: 1 - i * 0.12 }}>⚡ {l.text}</li>
      ))}
    </ul>
  </div>
);

export default function NeuralCortex3D({ p, linkState = "online", height = 460, chrome = false, contextUrl, authToken, health, levelRef }) {
  const [ctx, setCtx] = useState(null);
  const [orbit, setOrbit] = useState(true);
  const [lapse, setLapse] = useState(false);
  const [hud, setHud] = useState({ rate: 0, log: [], focus: "RESTING-STATE SCAN" });
  const burst = useRef([]);
  const seen = useRef(null);
  const spikeLog = useRef({ times: [], log: [], focus: null });

  useEffect(() => {
    if (!contextUrl) return undefined;
    const load = () => fetch(contextUrl, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} })
      .then((r) => (r.ok ? r.json() : null)).then((d) => {
        if (!d) return;
        const fresh = (d.recent || []).filter((u) => seen.current && u.ts > seen.current);
        fresh.forEach((u) => burst.current.push(u.region));
        if (d.recent?.[0]) seen.current = d.recent[0].ts;
        else if (!seen.current) seen.current = new Date().toISOString();
        setCtx(d);
      }).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [contextUrl, authToken, linkState]);

  useEffect(() => {
    const t = setInterval(() => {
      const now = performance.now();
      const s = spikeLog.current;
      s.times = s.times.filter((x) => now - x < 2000);
      setHud({ rate: s.times.length / 2, log: s.log.slice(0, 6), focus: s.focus || "RESTING-STATE SCAN" });
    }, 500);
    return () => clearInterval(t);
  }, []);

  const onSpike = (a, b) => {
    const s = spikeLog.current;
    s.times.push(performance.now());
    s.log.unshift({ text: `${a.label} → ${b.label}`, color: a.color });
    s.log = s.log.slice(0, 8);
    s.focus = linkState === "online" ? null : linkState === "speaking" ? `SPEECH · ${a.label}` : b.label;
  };

  const regions = useMemo(() => {
    const list = ctx?.regions?.length ? ctx.regions : (p?.stages || []).map((x) => x.title);
    return list.slice(0, MAX_R - 1);
  }, [ctx?.regions?.join("|"), p?.stages?.length]); // eslint-disable-line
  const scores = ctx?.scores || {};
  const coherence = regions.length ? Math.round((100 * Object.keys(scores).length) / regions.length) : 0;
  const stateLabel = { online: "RESTING STATE", thinking: "REASONING", speaking: "SPEAKING" }[linkState] || linkState;

  return (
    <div className="relative bg-canvas overflow-hidden" style={{ height }} data-testid="neural-cortex">
      {regions.length > 0 && (
        <Canvas camera={{ position: [0, 0.7, 5.4], fov: 50 }} gl={{ antialias: true }} dpr={[1, 2]}>
          <color attach="background" args={["#000000"]} />
          <fog attach="fog" args={["#000000", 7, 17]} />
          <Room />
          <Brain regions={regions} scores={scores} linkState={linkState} burstRef={burst} onSpike={onSpike} timeLapse={lapse} levelRef={levelRef} />
          <OrbitControls enablePan={false} enableDamping autoRotate={orbit} autoRotateSpeed={lapse ? 2.4 : 0.6} minDistance={2.6} maxDistance={12} />
          <EffectComposer>
            <Bloom mipmapBlur intensity={1.35} luminanceThreshold={0.08} luminanceSmoothing={0.4} radius={0.75} />
          </EffectComposer>
        </Canvas>
      )}
      {chrome && (
        <>
          <div className="absolute top-0 inset-x-0 flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/90 to-transparent z-10">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em]">
              <span className="w-1.5 h-1.5 rounded-full bg-accent2 animate-pulse" />
              <span className="text-white">MARI CORTEX · LIVE</span>
              <span className="px-1.5 py-0.5 border border-[#FF2BD6] text-[#FF2BD6] text-[9px]" data-testid="cortex-link-state">{stateLabel}</span>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setOrbit(!orbit)} data-testid="cortex-auto-orbit" className={`font-mono text-[9px] tracking-[0.15em] px-2 py-1 border transition-colors ${orbit ? "border-accent2 text-accent2" : "border-white/20 text-white/40"}`}>AUTO ORBIT</button>
              <button onClick={() => setLapse(!lapse)} data-testid="cortex-time-lapse" className={`font-mono text-[9px] tracking-[0.15em] px-2 py-1 border transition-colors ${lapse ? "border-[#FF2BD6] text-[#FF2BD6]" : "border-white/20 text-white/40"}`}>TIME-LAPSE</button>
            </div>
          </div>
          {health && (
            <div className="absolute top-9 left-3 right-3 flex items-center gap-3 font-mono text-[9.5px] tracking-[0.14em] z-10" data-testid="cortex-health">
              <span className="w-2 h-2 rounded-full" style={{ background: health.color, boxShadow: `0 0 8px ${health.color}` }} />
              <span style={{ color: health.color }}>{health.label} {health.score}/100</span>
              <span className="text-white/40 truncate">{health.detail}</span>
            </div>
          )}
          <Telemetry rate={hud.rate} coherence={coherence} focus={hud.focus} log={hud.log} />
          <div className="absolute bottom-2 left-3 font-mono text-[9px] tracking-[0.18em] text-white/35 z-10">{regions.length} REGIONS · {Object.keys(scores).length} ACTIVE · {ctx?.model || "…"}</div>
          <div className="absolute bottom-2 right-3 font-mono text-[9px] tracking-[0.12em] text-white/30 z-10">scroll = deep zoom · drag = rotate</div>
        </>
      )}
    </div>
  );
}
